import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

import { normalizeRequiredText } from './form-value-normalizers.util';

export const URL_VALIDATION_ERROR_KEY = 'invalidUrl';

export interface UrlValidatorOptions {
  allowedProtocols?: readonly string[];
}

const DEFAULT_ALLOWED_PROTOCOLS = ['http:', 'https:'] as const;

export function urlValidator(options?: UrlValidatorOptions): ValidatorFn {
  const allowedProtocols = normalizeAllowedProtocols(options?.allowedProtocols ?? DEFAULT_ALLOWED_PROTOCOLS);

  return (control: AbstractControl): ValidationErrors | null => {
    const rawValue = normalizeRequiredText(String(control.value ?? ''));
    if (!rawValue) {
      return null;
    }

    const lowerCasedValue = rawValue.toLowerCase();
    const startsWithAllowedProtocol = Array.from(allowedProtocols).some((protocol) =>
      lowerCasedValue.startsWith(`${protocol.slice(0, -1)}://`)
    );

    if (!startsWithAllowedProtocol) {
      return { [URL_VALIDATION_ERROR_KEY]: true };
    }

    try {
      const parsedUrl = new URL(rawValue);
      if (!parsedUrl.hostname || !allowedProtocols.has(parsedUrl.protocol)) {
        return { [URL_VALIDATION_ERROR_KEY]: true };
      }

      return null;
    } catch {
      return { [URL_VALIDATION_ERROR_KEY]: true };
    }
  };
}

export function httpHttpsUrlValidator(): ValidatorFn {
  return urlValidator({ allowedProtocols: DEFAULT_ALLOWED_PROTOCOLS });
}

export function trimmedRequiredValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null =>
    normalizeRequiredText(String(control.value ?? '')).length > 0 ? null : { required: true };
}

export function trimmedMinLengthValidator(minLength: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const normalizedValue = normalizeRequiredText(String(control.value ?? ''));
    if (!normalizedValue.length || normalizedValue.length >= minLength) {
      return null;
    }

    return {
      minlength: {
        requiredLength: minLength,
        actualLength: normalizedValue.length,
      },
    };
  };
}

export function trimmedMaxLengthValidator(maxLength: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const normalizedValue = normalizeRequiredText(String(control.value ?? ''));
    if (normalizedValue.length <= maxLength) {
      return null;
    }

    return {
      maxlength: {
        requiredLength: maxLength,
        actualLength: normalizedValue.length,
      },
    };
  };
}

function normalizeAllowedProtocols(protocols: readonly string[]): Set<string> {
  return new Set(
    protocols
      .map((protocol) => protocol.trim().toLowerCase())
      .filter((protocol) => protocol.length > 0)
      .map((protocol) => (protocol.endsWith(':') ? protocol : `${protocol}:`))
  );
}
